import React, { useState, useEffect } from 'react';
import { ReceiptReviewService } from '@/services/receipt-review.service';
import {
  ReceiptReview as ReceiptReviewModel,
  ReviewQueueStats,
  FraudDetectionResult,
} from '@/models/receipt-review.model';
import { ReceiptStatus } from '@/models/receipt.model';
import { useAuth } from '@/contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

const StatusLabels: Record<ReceiptStatus, string> = {
  [ReceiptStatus.pending]: 'Pending',
  [ReceiptStatus.inReview]: 'In Review',
  [ReceiptStatus.approved]: 'Approved',
  [ReceiptStatus.rejected]: 'Rejected',
  [ReceiptStatus.paid]: 'Paid',
};

export const ReceiptReview: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'queue' | 'history'>('queue');
  const [queue, setQueue] = useState<ReceiptReviewModel[]>([]);
  const [history, setHistory] = useState<ReceiptReviewModel[]>([]);
  const [stats, setStats] = useState<ReviewQueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptReviewModel | null>(null);
  const [fraudResult, setFraudResult] = useState<FraudDetectionResult | null>(null);
  const [filter, setFilter] = useState<'all' | 'high_priority' | 'fraud' | 'duplicates'>('all');

  useEffect(() => {
    loadQueue();
  }, []);

  useEffect(() => {
    if (selectedReceipt) {
      ReceiptReviewService.detectFraud(selectedReceipt).then(setFraudResult);
    }
  }, [selectedReceipt]);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const [queueData, historyData, statsData] = await Promise.all([
        ReceiptReviewService.getReviewQueue(),
        ReceiptReviewService.getProcessedReceipts(),
        ReceiptReviewService.getReviewQueueStats(),
      ]);
      setQueue(queueData);
      setHistory(historyData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading review queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (receiptId: string, amount: number, notes?: string) => {
    if (!profile) return;

    try {
      await ReceiptReviewService.approveReceipt(receiptId, amount, profile.id, notes);
      await loadQueue();
      setSelectedReceipt(null);
    } catch (error) {
      console.error('Error approving receipt:', error);
      alert('Failed to approve receipt');
    }
  };

  const handleReject = async (receiptId: string, reason: string, notes?: string) => {
    if (!profile) return;

    try {
      await ReceiptReviewService.rejectReceipt(receiptId, reason, profile.id, notes);
      await loadQueue();
      setSelectedReceipt(null);
    } catch (error) {
      console.error('Error rejecting receipt:', error);
      alert('Failed to reject receipt');
    }
  };

  const handleBulkApprove = async (receiptIds: string[]) => {
    if (!profile) return;
    if (!confirm(`Approve ${receiptIds.length} receipts?`)) return;

    try {
      await ReceiptReviewService.bulkApproveReceipts(receiptIds, profile.id);
      await loadQueue();
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('Failed to bulk approve receipts');
    }
  };

  const filteredQueue = queue.filter(receipt => {
    if (filter === 'high_priority') {
      return receipt.requiresReview || (receipt.fraudScore && receipt.fraudScore > 70);
    }
    if (filter === 'fraud') {
      return receipt.fraudScore && receipt.fraudScore > 50;
    }
    if (filter === 'duplicates') {
      return receipt.isDuplicate;
    }
    return true;
  });

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading review queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Receipt Review & Fraud Detection</h1>
          <p className="mt-1 text-sm text-gray-500">Review and approve receipts for reimbursement</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Review Queue Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <StatCard label="Total" value={stats.total} icon="📋" color="blue" />
              <StatCard label="Pending" value={stats.pending} icon="⏳" color="yellow" />
              <StatCard label="In Review" value={stats.inReview} icon="👀" color="purple" />
              <StatCard label="High Priority" value={stats.highPriority} icon="🚨" color="red" />
              <StatCard label="Duplicates" value={stats.duplicates} icon="🔄" color="gray" />
              <StatCard label="Processed Today" value={stats.processedToday || 0} icon="✅" color="green" />
              <StatCard label="Avg Time (hrs)" value={stats.avgProcessingTime || 0} icon="⏱️" color="indigo" />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('queue')}
              className={`${activeTab === 'queue'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Review Queue ({queue.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Processing History
            </button>
          </nav>
        </div>

        {activeTab === 'queue' ? (
          <>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow mb-6 p-4">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Filter:</label>
                {(['all', 'high_priority', 'fraud', 'duplicates'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${filter === f
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            {/* Review Queue */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Review Queue ({filteredQueue.length})
                </h3>
                {filteredQueue.length > 0 && (
                  <button
                    onClick={() => {
                      const ids = filteredQueue
                        .filter(r => {
                          if (typeof r.status === 'string') {
                            return r.status === 'pending';
                          }
                          return r.status === 0; // 0 = pending
                        })
                        .slice(0, 10)
                        .map(r => r.receiptId);
                      if (ids.length > 0) {
                        handleBulkApprove(ids);
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Bulk Approve (Top 10)
                  </button>
                )}
              </div>
              {filteredQueue.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No receipts in queue</h3>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredQueue.map((receipt) => (
                    <ReceiptRow
                      key={receipt.receiptId}
                      receipt={receipt}
                      onView={async () => {
                        const full = await ReceiptReviewService.getReceiptForReview(receipt.receiptId);
                        if (full) setSelectedReceipt(full);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* History View */
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Processed Receipts (Last 50)
              </h3>
            </div>
            {history.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No processing history yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider/Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewed By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processing Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.map((item) => (
                      <tr key={item.receiptId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.reviewedAt ? new Date(item.reviewedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>{item.providerName || 'Unknown'}</div>
                          <div className="text-xs text-gray-400">{item.categoryName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${typeof item.status === 'string'
                              ? (item.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')
                              : (item.status === 2 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')
                            }`}>
                            {typeof item.status === 'string' ? item.status : (item.status === 2 ? 'Approved' : 'Rejected')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.reviewedByName || item.reviewedBy || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.reviewedAt && item.submittedAt
                            ? `${((new Date(item.reviewedAt).getTime() - new Date(item.submittedAt).getTime()) / (1000 * 60 * 60)).toFixed(1)} hrs`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Receipt Detail Modal */}
      {selectedReceipt && (
        <ReceiptDetailModal
          receipt={selectedReceipt}
          fraudResult={fraudResult}
          onClose={() => {
            setSelectedReceipt(null);
            setFraudResult(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'orange' | 'purple' | 'gray' | 'indigo';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="text-3xl opacity-75">{icon}</div>
      </div>
    </div>
  );
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

interface ReceiptRowProps {
  receipt: ReceiptReviewModel;
  onView: () => void;
}

const ReceiptRow: React.FC<ReceiptRowProps> = ({ receipt, onView }) => {
  const getPriorityBadge = () => {
    const hoursSinceSubmission = (new Date().getTime() - new Date(receipt.submittedAt).getTime()) / (1000 * 60 * 60);

    return (
      <div className="flex space-x-2">
        {receipt.fraudScore && receipt.fraudScore > 70 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">High Risk</span>
        )}
        {receipt.requiresReview && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Review Required</span>
        )}
        {receipt.isDuplicate && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Duplicate</span>
        )}
        {hoursSinceSubmission > 48 ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 animate-pulse">SLA Alert</span>
        ) : hoursSinceSubmission > 24 ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Overdue</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer" onClick={onView}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h4 className="text-lg font-semibold text-gray-900">
              {formatCurrency(receipt.amount)}
            </h4>
            {getPriorityBadge()}
            {receipt.fraudScore && receipt.fraudScore > 50 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                Fraud Score: {receipt.fraudScore}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {receipt.providerName || 'Unknown Provider'}
            {receipt.categoryName && ` • ${receipt.categoryName}`}
          </p>
          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
            {receipt.serviceDate && (
              <span>Service Date: {new Date(receipt.serviceDate).toLocaleDateString()}</span>
            )}
            <span>•</span>
            <span>Submitted: {new Date(receipt.submittedAt).toLocaleDateString()}</span>
            {receipt.reviewedBy && (
              <>
                <span>•</span>
                <span>Reviewed by {receipt.reviewedByName || 'Unknown'}</span>
              </>
            )}
          </div>
        </div>
        <div className="ml-4">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${(typeof receipt.status === 'string' && receipt.status === 'pending') ||
              (typeof receipt.status === 'number' && receipt.status === 0)
              ? 'bg-yellow-100 text-yellow-800'
              : (typeof receipt.status === 'number' && receipt.status === 1)
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
            {typeof receipt.status === 'string'
              ? StatusLabels[receipt.status as ReceiptStatus] || receipt.status
              : (receipt.status === 0 ? 'Pending' : receipt.status === 1 ? 'In Review' : receipt.status === 2 ? 'Approved' : receipt.status === 3 ? 'Rejected' : 'Paid')}
          </span>
        </div>
      </div>
    </div>
  );
};

interface ReceiptDetailModalProps {
  receipt: ReceiptReviewModel;
  fraudResult: FraudDetectionResult | null;
  onClose: () => void;
  onApprove: (receiptId: string, amount: number, notes?: string) => void;
  onReject: (receiptId: string, reason: string, notes?: string) => void;
}

const ReceiptDetailModal: React.FC<ReceiptDetailModalProps> = ({
  receipt,
  fraudResult,
  onClose,
  onApprove,
  onReject,
}) => {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [amount, setAmount] = useState(receipt.amount.toString());
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [calculationReasoning, setCalculationReasoning] = useState<string | null>(null);
  const [isLoadingCalculation, setIsLoadingCalculation] = useState(false);

  // Calculate reimbursement when approve action is selected
  useEffect(() => {
    if (action === 'approve' && !calculationReasoning && !isLoadingCalculation) {
      const calculateRefund = async () => {
        setIsLoadingCalculation(true);
        try {
          const functions = getFunctions();
          const calculateFn = httpsCallable(functions, 'calculateReimbursement');

          const result = await calculateFn({ receiptId: receipt.receiptId });
          const data = result.data as any;

          const calculatedAmount = data.approvedAmount || 0;
          const reasoning = data.reasoning || 'Calculation completed';

          setAmount(calculatedAmount.toFixed(2));
          setCalculationReasoning(reasoning);
        } catch (error) {
          console.error('Error calculating reimbursement:', error);
          setCalculationReasoning('Could not calculate refund amount. Using receipt amount.');
          setAmount(receipt.amount.toString());
        } finally {
          setIsLoadingCalculation(false);
        }
      };

      calculateRefund();
    }
  }, [action, receipt.receiptId, receipt.amount, calculationReasoning, isLoadingCalculation]);

  const handleSubmit = () => {
    if (action === 'approve') {
      const approveAmount = parseFloat(amount) || receipt.amount;
      onApprove(receipt.receiptId, approveAmount, notes);
    } else if (action === 'reject') {
      if (!reason.trim()) {
        alert('Please provide a rejection reason');
        return;
      }
      onReject(receipt.receiptId, reason, notes);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Receipt Review</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Fraud Detection */}
            {fraudResult && (
              <div className={`p-4 rounded-lg border-2 ${fraudResult.isFraud
                  ? 'bg-red-50 border-red-200'
                  : fraudResult.score > 30
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-green-50 border-green-200'
                }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Fraud Detection</h3>
                  <span className={`text-lg font-bold ${fraudResult.isFraud ? 'text-red-600' : fraudResult.score > 30 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                    Score: {fraudResult.score}/100
                  </span>
                </div>
                {fraudResult.flags.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700 mb-1">Flags:</p>
                    <div className="flex flex-wrap gap-2">
                      {fraudResult.flags.map((flag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-white rounded text-xs">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {fraudResult.reasons.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700 mb-1">Reasons:</p>
                    <ul className="list-disc list-inside text-sm text-gray-600">
                      {fraudResult.reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Receipt Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Amount</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(receipt.amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1 text-sm text-gray-900">
                  {typeof receipt.status === 'string'
                    ? StatusLabels[receipt.status as ReceiptStatus] || receipt.status
                    : (receipt.status === 0 ? 'Pending' : receipt.status === 1 ? 'In Review' : receipt.status === 2 ? 'Approved' : receipt.status === 3 ? 'Rejected' : 'Paid')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Provider</label>
                <p className="mt-1 text-sm text-gray-900">{receipt.providerName || 'Unknown'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="mt-1 text-sm text-gray-900">{receipt.categoryName || 'Unknown'}</p>
              </div>
              {receipt.serviceDate && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Service Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(receipt.serviceDate).toLocaleDateString()}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">Submitted</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(receipt.submittedAt).toLocaleString()}</p>
              </div>
            </div>

            {/* Receipt Image */}
            {receipt.receiptImageUrl && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">Receipt Image</label>
                <img
                  src={receipt.receiptImageUrl}
                  alt="Receipt"
                  className="max-w-full h-auto border border-gray-300 rounded-lg"
                />
              </div>
            )}

            {/* OCR Text */}
            {receipt.ocrText && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">OCR Text</label>
                <div className="p-3 bg-gray-50 rounded-lg text-sm font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {receipt.ocrText}
                </div>
              </div>
            )}

            {/* Review Actions */}
            <div className="border-t pt-6">
              {!action ? (
                <div className="flex space-x-4">
                  <button
                    onClick={() => setAction('approve')}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setAction('reject')}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {action === 'approve' && (
                    <>
                      {/* Calculation breakdown display */}
                      {isLoadingCalculation && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-blue-800">Calculating refund amount...</span>
                          </div>
                        </div>
                      )}

                      {calculationReasoning && !isLoadingCalculation && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex items-start space-x-2">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-blue-900 mb-1">
                                Calculated Refund Amount
                              </h4>
                              <p className="text-sm text-blue-800">
                                {calculationReasoning}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Approved Amount * <span className="text-gray-500 text-xs">(Editable)</span>
                        </label>
                        <input
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          You can edit this amount if needed
                        </p>
                      </div>
                    </>
                  )}
                  {action === 'reject' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason *
                      </label>
                      <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setAction(null);
                        setAmount(receipt.amount.toString());
                        setReason('');
                        setNotes('');
                        setCalculationReasoning(null);
                        setIsLoadingCalculation(false);
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      className={`flex-1 px-4 py-2 text-white rounded-md ${action === 'approve'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-red-600 hover:bg-red-700'
                        }`}
                    >
                      {action === 'approve' ? 'Approve Receipt' : 'Reject Receipt'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
